import { c as _c } from "react/compiler-runtime";
import { CheckCircle2, HelpCircle, Info, MinusCircle, OctagonAlert, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { ReasonResources } from './ReasonResources';
import type { ExportReason, ExportReasonResource, ExportReasonResponse, ReasonApplicability, ReasonExtension, ReasonImportance } from './types';
export interface ReasonCardAccent {
  /** Left accent bar colour when untouched. */
  barIdle: string;
  /** Left accent bar colour when flagged as applying. */
  barActive: string;
  /** Challenge callout background tint. */
  challengeBg: string;
  /** Challenge callout text colour. */
  challengeText: string;
  /** Challenge icon colour. */
  iconColor: string;
}

/** Props that control display behavior of ReasonCard (passable from parent). */
export interface ReasonCardDisplayProps {
  /** When true, resources are always visible regardless of selection state. */
  alwaysShowResources?: boolean;
}
interface ReasonCardProps extends ReasonCardDisplayProps {
  reason: ExportReason;
  accent: ReasonCardAccent;
  response?: ExportReasonResponse;
  /** Extra resources appended after the reason's own resources. */
  extraResources?: ExportReasonResource[];
  extension?: ReasonExtension;
  onChange: (reasonId: string, next: ExportReasonResponse) => void;
}
const IMPORTANCE_ICON: Record<ReasonImportance, {
  icon: typeof OctagonAlert;
  className: string;
  label: string;
}> = {
  critical: {
    icon: OctagonAlert,
    className: 'text-rose-500',
    label: 'Critical blocker'
  },
  high: {
    icon: TriangleAlert,
    className: 'text-amber-500',
    label: 'High importance'
  },
  moderate: {
    icon: Info,
    className: 'text-blue-400',
    label: 'Moderate importance'
  }
};
const APPLICABILITY_OPTIONS: Array<{
  value: ReasonApplicability;
  label: string;
  icon: typeof CheckCircle2;
  activeClass: string;
  idleClass: string;
}> = [{
  value: 'applies',
  label: 'Real for us',
  icon: CheckCircle2,
  activeClass: 'border-teal-700 bg-teal-700 text-white',
  idleClass: 'border-gray-300 bg-white text-gray-700 hover:bg-teal-50'
}, {
  value: 'not-a-concern',
  label: 'Not a concern',
  icon: MinusCircle,
  activeClass: 'border-gray-700 bg-gray-700 text-white',
  idleClass: 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
}, {
  value: 'unsure',
  label: 'Not sure',
  icon: HelpCircle,
  activeClass: 'border-gray-700 bg-gray-700 text-white',
  idleClass: 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
}];
export function ReasonCard(t0) {
  const $ = _c(73);
  const {
    reason,
    accent,
    response,
    extraResources: t1,
    extension,
    alwaysShowResources: t2,
    onChange
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const extraResources = t3;
  const alwaysShowResources = t2 === undefined ? false : t2;
  const applicability = response?.applicability;
  const showDetail = applicability === "applies" || applicability === "unsure";
  let t4;
  if ($[2] !== reason.resources) {
    t4 = reason.resources ?? [];
    $[2] = reason.resources;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== extraResources || $[5] !== t4) {
    t5 = [...t4, ...extraResources];
    $[4] = extraResources;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const resources = t5;
  let t6;
  if ($[7] !== extension || $[8] !== reason || $[9] !== response) {
    t6 = typeof extension === "function" ? extension({
      reason,
      response
    }) : extension;
    $[7] = extension;
    $[8] = reason;
    $[9] = response;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const resolvedExtension = t6;
  let t7;
  if ($[11] !== applicability || $[12] !== onChange || $[13] !== reason.id || $[14] !== response) {
    t7 = function setApplicability(value) {
      if (applicability === value) {
        onChange(reason.id, {});
        return;
      }
      onChange(reason.id, {
        ...response,
        applicability: value
      });
    };
    $[11] = applicability;
    $[12] = onChange;
    $[13] = reason.id;
    $[14] = response;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const setApplicability = t7;
  let t8;
  if ($[16] !== onChange || $[17] !== reason.id || $[18] !== response) {
    t8 = function setNote(note) {
      onChange(reason.id, {
        ...response,
        note: note || undefined
      });
    };
    $[16] = onChange;
    $[17] = reason.id;
    $[18] = response;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const setNote = t8;
  let t9;
  if ($[20] !== accent.barActive || $[21] !== accent.barIdle || $[22] !== applicability) {
    t9 = cn("rounded-lg border border-l-[3px] bg-white p-5 transition-colors", applicability === "applies" ? cn("border-gray-300", accent.barActive) : cn("border-gray-200", accent.barIdle));
    $[20] = accent.barActive;
    $[21] = accent.barIdle;
    $[22] = applicability;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const imp = IMPORTANCE_ICON[reason.importance];
  const ImpIcon = imp.icon;
  let t10;
  if ($[24] !== imp.className) {
    t10 = cn("h-4.5 w-4.5 shrink-0", imp.className);
    $[24] = imp.className;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== ImpIcon || $[27] !== imp.label || $[28] !== t10) {
    t11 = <ImpIcon className={t10} aria-label={imp.label} />;
    $[26] = ImpIcon;
    $[27] = imp.label;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const t12 = t11;
  let t13;
  if ($[30] !== reason.title || $[31] !== t12) {
    t13 = <h4 className="flex items-center gap-2 text-lg font-bold leading-snug text-gray-900 sm:text-xl">{t12}{reason.title}</h4>;
    $[30] = reason.title;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== reason.summary) {
    t14 = <p className="mt-1.5 text-[15px] leading-relaxed text-gray-600">{reason.summary}</p>;
    $[33] = reason.summary;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== accent.challengeBg) {
    t15 = cn("mt-3 flex items-start gap-2 rounded-lg px-3 py-2.5", accent.challengeBg);
    $[35] = accent.challengeBg;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== accent.iconColor) {
    t16 = cn("mt-0.5 h-4 w-4 shrink-0", accent.iconColor);
    $[37] = accent.iconColor;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t16) {
    t17 = <HelpCircle className={t16} />;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== accent.challengeText) {
    t18 = cn("text-sm font-medium italic leading-snug", accent.challengeText);
    $[41] = accent.challengeText;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== reason.challenge || $[44] !== t18) {
    t19 = <p className={t18}>{reason.challenge}</p>;
    $[43] = reason.challenge;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t15 || $[47] !== t17 || $[48] !== t19) {
    t20 = <div className={t15}>{t17}{t19}</div>;
    $[46] = t15;
    $[47] = t17;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== applicability || $[51] !== setApplicability) {
    t21 = APPLICABILITY_OPTIONS.map(option => {
      const Icon = option.icon;
      const active = applicability === option.value;
      return <button key={option.value} type="button" onClick={() => setApplicability(option.value)} aria-pressed={active} className={cn("inline-flex items-center gap-1.5 rounded-lg border-2 px-3.5 py-2 text-sm font-semibold transition-all cursor-pointer", active ? option.activeClass : option.idleClass)}><Icon className="h-4 w-4" />{option.label}</button>;
    });
    $[50] = applicability;
    $[51] = setApplicability;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t21) {
    t22 = <div className="mt-4 flex flex-wrap gap-2">{t21}</div>;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== resolvedExtension || $[56] !== resources || $[57] !== response?.note || $[58] !== setNote || $[59] !== showDetail) {
    t23 = showDetail && <div className="mt-4 space-y-3 border-t border-dashed border-gray-200 pt-4"><Textarea rows={2} value={response?.note ?? ""} onChange={event => setNote(event.target.value)} placeholder={"Add a note \u2014 the honest situation, or what would move this forward"} className="text-sm" />{resolvedExtension && <div>{resolvedExtension}</div>}{resources.length > 0 && <ReasonResources resources={resources} />}</div>;
    $[55] = resolvedExtension;
    $[56] = resources;
    $[57] = response?.note;
    $[58] = setNote;
    $[59] = showDetail;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== alwaysShowResources || $[62] !== resources || $[63] !== showDetail) {
    t24 = alwaysShowResources && !showDetail && resources.length > 0 && <div className="mt-4 border-t border-dashed border-gray-200 pt-4"><ReasonResources resources={resources} /></div>;
    $[61] = alwaysShowResources;
    $[62] = resources;
    $[63] = showDetail;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t13 || $[66] !== t14 || $[67] !== t20 || $[68] !== t22 || $[69] !== t23 || $[70] !== t24 || $[71] !== t9) {
    t25 = <div className={t9}>{t13}{t14}{t20}{t22}{t23}{t24}</div>;
    $[65] = t13;
    $[66] = t14;
    $[67] = t20;
    $[68] = t22;
    $[69] = t23;
    $[70] = t24;
    $[71] = t9;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  return t25;
}